import React from "react";
import { CircleAlertIcon, Loader2Icon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { ResponseError, otpRetryAt, otpWaitSeconds } from "@norma-bi/bi-api";
import { useRootAPI, useUnauthenticatedAPI } from "@/api";
import { useAuth } from "@/Auth";
import { useAccountNumberInput } from "@/hooks/useAccountNumberInput";
import { useEmailInput } from "@/hooks/useEmailInput";
import { customToast } from "@/utils/customToast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useT } from "@/i18n";

type AddBusinessDialogProps = {
  onClose: () => void;
};

export function AddBusinessDialog({ onClose }: AddBusinessDialogProps) {
  const auth = useAuth();
  const loginAPI = useUnauthenticatedAPI();
  const rootAPI = useRootAPI();
  const queryClient = useQueryClient();
  const id = React.useId();
  const { t, formatNumber } = useT();
  /** The OTP wait as m:ss, in the interface's digits. */
  const formatWait = (seconds: number) =>
    `${formatNumber(Math.floor(seconds / 60))}:${formatNumber(seconds % 60, { minimumIntegerDigits: 2 })}`;
  const { accountNumber, handleAccountNumberChange, isAccountNumberValid } =
    useAccountNumberInput();
  const { email, handleEmailChange, isEmailValid } = useEmailInput(
    auth.isAuthenticated ? (auth.user.identities[0]?.email ?? "") : "",
  );
  const [challengeID, setChallengeID] = React.useState<string>();
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [requestRetryAt, setRequestRetryAt] = React.useState<Date>();
  const [verifyRetryAt, setVerifyRetryAt] = React.useState<Date>();
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const requestWait = otpWaitSeconds(requestRetryAt, now);
  const verifyWait = otpWaitSeconds(verifyRetryAt, now);

  async function submit(requestNewCode = false) {
    setBusy(true);
    setError(undefined);
    try {
      if (requestNewCode || !challengeID) {
        const result = await loginAPI.postLoginOtpChallenge({
          RequestOTPChallengeRequest: { business_ref: Number(accountNumber), email },
        });
        if (result.status === "code_sent" && result.challenge_id) {
          setChallengeID(result.challenge_id);
          setCode("");
          setRequestRetryAt(result.retry_at);
          setVerifyRetryAt(undefined);
        } else if (result.status === "business_not_found") {
          setError(t("businesses.add.businessNotFound"));
        } else if (result.status === "user_not_found") {
          setError(t("businesses.add.userNotFound"));
        } else {
          setError(t("businesses.add.codeNotSent"));
        }
        return;
      }

      const result = await rootAPI.postOrganizationBusinessesOtpVerify({
        AddBusinessOTPRequest: { challenge_id: challengeID, code },
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["businesses"] }),
        queryClient.invalidateQueries({ queryKey: ["self"] }),
      ]);
      customToast(
        result.status === "installing"
          ? t("businesses.add.installing")
          : result.status === "moved"
            ? t("businesses.add.moved")
            : t("businesses.add.alreadyHere"),
        "success",
        "bottom",
      );
      onClose();
    } catch (caught) {
      const retryAt = await otpRetryAt(caught);
      if (retryAt && (requestNewCode || !challengeID)) setRequestRetryAt(retryAt);
      if (caught instanceof ResponseError) {
        if (caught.response.status === 400) {
          setError(t("businesses.add.badCode"));
        } else if (caught.response.status === 403) {
          setError(t("businesses.add.otherAdminMustVerify"));
        } else if (caught.response.status === 409) {
          setError(t("businesses.add.codeUsed"));
        } else if (caught.response.status === 429) {
          if (challengeID && !requestNewCode) setVerifyRetryAt(retryAt);
          setError(t("businesses.add.waitForCountdown"));
        } else {
          setError(
            challengeID ? t("businesses.add.unexpectedVerify") : t("businesses.add.unexpectedSend"),
          );
        }
      } else {
        setError(
          challengeID ? t("businesses.add.unconfirmedVerify") : t("businesses.add.unconfirmedSend"),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  const disableIdentity = Boolean(challengeID) || busy;

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent
        showCloseButton={!busy}
        onEscapeKeyDown={busy ? (event) => event.preventDefault() : undefined}
        onInteractOutside={busy ? (event) => event.preventDefault() : undefined}
        // Long errors and the code field can outgrow a phone: scroll only the body.
        className="max-h-[90dvh] grid-rows-[minmax(0,1fr)] overflow-hidden sm:max-w-md"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          className="grid min-h-0 grid-rows-[auto_minmax(0,1fr)_auto]"
        >
          <DialogHeader>
            <DialogTitle className="pe-6">{t("businesses.add.title")}</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-0 flex-col gap-5 overflow-y-auto px-4 py-4">
            <DialogDescription>{t("businesses.add.description")}</DialogDescription>
            {error && (
              <Alert variant="destructive">
                <CircleAlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor={`${id}-reference`}>{t("businesses.add.reference")}</FieldLabel>
                <Input
                  id={`${id}-reference`}
                  value={accountNumber}
                  onChange={handleAccountNumberChange}
                  inputMode="numeric"
                  dir="ltr"
                  disabled={disableIdentity}
                  required
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={`${id}-email`}>{t("businesses.add.email")}</FieldLabel>
                <Input
                  id={`${id}-email`}
                  type="email"
                  dir="ltr"
                  value={email}
                  onChange={handleEmailChange}
                  disabled={disableIdentity}
                  required
                />
              </Field>
              {challengeID && (
                <Field>
                  <FieldLabel htmlFor={`${id}-code`}>{t("businesses.add.code")}</FieldLabel>
                  <Input
                    id={`${id}-code`}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    dir="ltr"
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                    maxLength={6}
                    autoFocus
                    required
                  />
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="self-start px-0"
                    disabled={busy || requestWait > 0}
                    onClick={() => {
                      void submit(true);
                    }}
                  >
                    {requestWait > 0
                      ? t("businesses.add.requestNewCodeIn", { time: formatWait(requestWait) })
                      : t("businesses.add.requestNewCode")}
                  </Button>
                </Field>
              )}
            </FieldGroup>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                busy ||
                !isAccountNumberValid ||
                Number(accountNumber) <= 0 ||
                !isEmailValid ||
                Boolean(challengeID && (code.length !== 6 || verifyWait > 0)) ||
                Boolean(!challengeID && requestWait > 0)
              }
            >
              {busy && <Loader2Icon data-icon="inline-start" className="animate-spin" />}
              {busy
                ? t("common.pleaseWait")
                : challengeID
                  ? verifyWait > 0
                    ? t("businesses.add.tryAgainIn", { time: formatWait(verifyWait) })
                    : t("businesses.add.submit")
                  : requestWait > 0
                    ? t("businesses.add.sendCodeIn", { time: formatWait(requestWait) })
                    : t("businesses.add.sendCode")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
